import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  forwardRef,
  ElementRef,
} from "@angular/core";

import { AnimationEvent } from "@angular/animations";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  ActiveUser,
  ActiveUserProfilePromiseService,
  BmarkClientOrdersTableDefinitionFactory,
  BmarkFixingData,
  CcyLookupService,
  ProfilePromiseActionService,
  TraderGroupLookupService,
  SystemConfigPromiseService,
  SysFeatures,
  BmarkFixingType,
  ProfileSettingKeys,
  CubiqWorker,
  NotificationService,
  BenchmarkDataPrimaryLookupService,
} from "shared/cubiq-services";
import { TranslateService } from "@ngx-translate/core";
import { BmarkClientOrdersBlotterMode } from "../../shared/cubiq-services/model/benchmarks/enums/BmarkClientOrdersBlotterMode.enum";
import { BfxBlotterFilterBarComponent } from "../bfx-blotter-filter/bfx-blotter-filter-bar.component";
import { BfxBlotterFilterManagerComponent } from "../bfx-blotter-filter/bfx-blotter-filter-manager.component";
import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";
import { FilterType } from "../bfx-blotter-filter/bfx-blotter-filter.constants";
import { BfxBlotterFilterable } from "../bfx-blotter-filter/bfx-blotter-filterable";
import { ExtendedOrderTypeIdentifier } from "../bfx-blotter-filter/models/bfx-blotter-filter.constants";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";
import { BfxHedgeOrderMonitorComponentLauncher } from "../bfx-hedge-order-monitor/bfx-hedge-order-monitor.launcher";
import { AmountViewType } from "../bfx-menus/bfx-blotter-settings-menu.component";
import { GLComponentAlertStatus } from "../golden-layout/golden-layout-component";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { GoldenLayoutComponent } from "./../golden-layout/golden-layout-component";
import { BmarkClientOrdersDataService } from "./blotters/client-orders/services/bmark-client-orders-data.service";
import { BmarkHedgeOrdersDataService } from "./blotters/hedge-orders/services/bmark-hedge-orders-data.service";
import { BmarkSummaryClientOrdersComponent } from "./bmark-summary-client-orders.component";
import { BmarkSummaryHedgeOrdersComponent } from "./bmark-summary-hedge-orders.component";
import { BmarkSummarySpotRiskContainerComponent } from "./bmark-summary-spot-risk.component";
import { ChildBlotterComponent } from "./models/bmark-sub-blotter.interface";
import { BmarkSummaryAlertStatus } from "./models/bmark-summary-alert-status.model";
import {
  BmarkFixingsManagerService,
  FixingActiveTab,
  FixingTabType,
} from "./services/bmark-fixings-manager.service";
import { BmarkBlotterViewFilterSelectionService } from "./services/bmark-blotter-view-filter-selection.service";
import { BfxHedgeOrderMonitorComponent } from "../bfx-hedge-order-monitor/bfx-hedge-order-monitor.component";
import {
  BmarkSummaryUtilService,
  FilterAppliedStatusModel,
} from "./services/bmark-summary-util.service";
import { BfxHedgeOrderMonitorUtilService } from "../bfx-hedge-order-monitor/services/bfx-hedge-order-monitor-util.service";
import { Subject, filter, takeUntil } from "rxjs";
import {
  BmarkSummarySettingsMenuComponent,
  BmarkSummaryContainerSettingsMenuHost,
} from "./bmark-summary-settings-menu.component";
import { GridOptions, RowNode } from "ag-grid-community";
import {
  SpotRiskPair,
  SpotRiskModel,
} from "shared/cubiq-services/lookup/model/impl/bmrisk/SpotRiskModel";
import { BfxHedgeOrderMonitorComponentData } from "../bfx-hedge-order-monitor/bfx-hedge-order-monitor.component";
import { USERSETTINGS_CATEGORY_BMARKSUMMARY } from "../bfx-user-settings/bfx-user-settings.component";
import { BfxDialogData } from "../bfx-dialogs/models/bfx-dialog-data.model";
import { BfxDialogCommand } from "../bfx-dialogs/models/bfx-dialog-command.enum";
import { BmarkBlotterBaseFilteringService } from "./services/bmark-blotter-base-filtering.service";
import { debounceTime } from "rxjs";
import { Subscription } from "rxjs";

import { BfxBaseBlotter } from "../bfx-base-blotter/bfx-base-blotter";
import { BmarkHedgeOrdersBlotterComponent } from "./blotters/hedge-orders/bmark-hedge-orders-blotter.component";
import { BmarkClientOrdersContextMenuService } from "./blotters/client-orders/services/bmark-client-orders-context-menu.service";
import { BmarkHedgeOrdersContextMenuService } from "./blotters/hedge-orders/services/bmark-hedge-orders-context-menu.service";
import { BmarkClientOrdersBlotterComponent } from "./blotters/client-orders/bmark-client-orders-blotter.component";
import { BmarkSummaryViewFilterType } from "./models/bmark-summary-view-filter-type.enum";
import { LoggerService } from "@bfx/logger";
import { BmarkContainerBlotterId } from "../bfx-blotter-filter/models/bmark-container-blotter-id.enum";
import { Splitter } from "@bfx/primeng/splitter";
import { MenuItem } from "@bfx/primeng/api";
import { TieredMenu } from "@bfx/primeng/tieredmenu";
import { ResizeObserverSchedulerTarget } from "../../shared/helper-services/resize-observer-scheduler-target";
import { BfxFixingBlotterAuditComponent } from "app/bfx-bmark-summary/blotters/fixing-audit/bfx-fixing-blotter-audit.component";
import { DomHandler } from "@bfx/primeng/dom";
import { FilterConfiguration } from "../bfx-blotter-filter/FilterConfiguration";
import { BmarkSummaryFwdRiskContainerComponent } from "app/bfx-bmark-summary/bmark-summary-fwd-risk.component";
import { BmarkSummaryRiskFilterType } from "app/bfx-bmark-summary/models/bmark-summary-risk-filter-type.enum";

const logger = LoggerService.for(__dirname);

const DEFAULT_TAB_INDEX: number = 1; // clientOrders

const BM_CONTAINER_PROFILE_KEY = ProfileSettingKeys.userBMContainerPreferences;
const BM_CONFIG_VERSION = "1";

@Component({
  selector: "bmark-summary-container",
  templateUrl: "./html/bmark-summary-container.html",
  providers: [
    {
      provide: BmarkSummaryContainerSettingsMenuHost,
      useExisting: forwardRef(() => BmarkSummaryContainerComponent),
    },
  ],
  standalone: false,
})
export class BmarkSummaryContainerComponent
  extends BmarkSummaryContainerSettingsMenuHost
  implements GoldenLayoutComponent, BfxBlotterFilterable, OnInit, AfterViewInit, OnDestroy
{
  /**********************************************************************/
  /* DATA */
  public showSearch: boolean = false;
  public searchValue: string;

  public search: EventEmitter<string | void> = new EventEmitter<string | void>();
  public retrievingFixingData: boolean = false;
  public viewInitComplete: boolean = false;

  public control: {
    canViewSpotRisk: boolean;
    canViewFwdRisk: boolean;
    canViewClientOrders: boolean;
    canViewHedgeOrders: boolean;
    canViewBenchmarkAudit: boolean;
    canUseCircuitBreaker: boolean;
    hedgingEnabled: boolean;
    bmRiskRouteEnabled: boolean;
    isTrader: boolean;
    manualRiskRouting: boolean;
    tabsNumber: number;
    active: boolean;
    alerts: {
      hco: boolean;
      hcoIds: Set<string>;
      sco: boolean;
      scoIds: Set<string>;
      badgeCount: number;
      flash: boolean;
    };
    dealtRiskVisible: boolean;
    typeFilterVisible: boolean;
    copyClipboardVisible: boolean;
    fwdRiskVisible: boolean;
  } = {
    canViewSpotRisk: true,
    canViewFwdRisk: true,
    canViewClientOrders: true,
    canViewHedgeOrders: true,
    canViewBenchmarkAudit: true,
    canUseCircuitBreaker: false,
    hedgingEnabled: false,
    bmRiskRouteEnabled: false,
    isTrader: false,
    manualRiskRouting: false,
    tabsNumber: 0,
    active: false,
    alerts: {
      hco: false,
      hcoIds: null,
      sco: false,
      scoIds: null,
      badgeCount: 0,
      flash: false,
    },
    dealtRiskVisible: false,
    typeFilterVisible: false,
    copyClipboardVisible: false,
    fwdRiskVisible: false,
  };

  public data: {
    activeFixingData: BmarkFixingData;
    activeFixingTitle: string;
    activeTableView: BmarkClientOrdersBlotterMode;
    tabIndex: number;
    viewFilterType: BmarkSummaryViewFilterType;
    viewFilterRequestPending: boolean;
    autoHedgingEnabled: boolean;
    fwdRiskFilter: BmarkSummaryRiskFilterType;
  } = {
    activeFixingData: null,
    activeFixingTitle: "",
    activeTableView: BmarkClientOrdersBlotterMode.DEALT,
    tabIndex: DEFAULT_TAB_INDEX,
    viewFilterType: BmarkSummaryViewFilterType.ALL,
    viewFilterRequestPending: false,
    autoHedgingEnabled: false,
    fwdRiskFilter: BmarkSummaryRiskFilterType.ALL,
  };

  public fixingsData: {
    clickedFixingData: BmarkFixingData;
  } = {
    clickedFixingData: null,
  };

  public label: {
    retrievingOrdersKey: string;
    retrievingOrders: string;
    button: {
      dealtViewLabelKey: string;
      dealtViewLabel: string;
      riskViewLabelKey: string;
      riskViewLabel: string;
      hedgeMonitorLabelKey: string;
      hedgeMonitorLabel: string;
      viewFilter: {
        all: string;
        active: string;
        filled: string;
      };
      copyToClipboardKey: string;
      copyToClipboard: string;
    };
    tab: {
      spotRiskLabelKey: string;
      spotRiskLabel: string;
      fwdRiskLabelKey: string;
      fwdRiskLabel: string;
      clientOrdersLabelKey: string;
      clientOrdersLabelBase: string;
      clientOrdersLabel: string;
      hedgeOrdersLabelKey: string;
      hedgeOrdersLabelBase: string;
      hedgeOrdersLabel: string;
      auditLabelBase: string;
      auditLabel: string;
    };
  } = {
    retrievingOrdersKey: "bmark_summary.container.retrieving_orders",
    retrievingOrders: "Retrieving Orders...",
    button: {
      dealtViewLabelKey: "bmark_summary.container.btn.dealt",
      dealtViewLabel: "Dealt View",
      riskViewLabelKey: "bmark_summary.container.btn.risk",
      riskViewLabel: "Risk View",
      hedgeMonitorLabelKey: "bmark_summary.container.btn.hedge",
      hedgeMonitorLabel: "Hedge Order Monitor",
      viewFilter: {
        all: "All",
        active: "Active",
        filled: "Filled",
      },
      copyToClipboardKey: "bmark_summary.container.btn.copy_clipboard",
      copyToClipboard: "Copy To Clipboard",
    },
    tab: {
      spotRiskLabelKey: "bmark_summary.container.tab.spot_risk",
      spotRiskLabel: "Spot Risk",
      fwdRiskLabelKey: "bmark_summary.container.tab.fwd_risk",
      fwdRiskLabel: "Forward Risk",
      clientOrdersLabelKey: "bmark_summary.container.tab.client_orders",
      clientOrdersLabelBase: "Client Orders",
      clientOrdersLabel: "Client Orders",
      hedgeOrdersLabelKey: "bmark_summary.container.tab.hedge_orders",
      hedgeOrdersLabelBase: "Hedge Orders",
      hedgeOrdersLabel: "Hedge Orders",
      auditLabelBase: "bmark_summary.container.tab.audit",
      auditLabel: "Audit",
    },
  };
  private viewFilterTypeItems: MenuItem[] = [];
  private tableViewItems: MenuItem[] = [];

  public blotterInstance: any = null;
  public blotterId: string;
  public filterConfiguration: FilterConfiguration = null;
  public extendedTypeId: ExtendedOrderTypeIdentifier = ExtendedOrderTypeIdentifier.BENCHMARK;
  public blotterFilterBar: BfxBlotterFilterBarComponent = null;
  private _subBlotterComponents: QueryList<ChildBlotterComponent>;
  private _activeContextMenuBlotter: BfxBaseBlotter;

  private quickSearchSubject: Subject<any> = new Subject<any>();

  // enum hoist for template
  public BmarkSummaryViewFilterType = BmarkSummaryViewFilterType;
  public BmarkFixingType = BmarkFixingType; //
  public BmarkClientOrdersBlotterMode = BmarkClientOrdersBlotterMode;

  protected onDestroy$: Subject<void> = new Subject<void>();

  // Splitter helper fields

  public height: number = 300;
  public minHeight: number = 100;
  /** Mouse Move handler for unlistening */
  private mouseMoveHandler: (e: MouseEvent) => void;
  /** Mouse up handler for unlistening  */
  private mouseUpHandler: (e: MouseEvent) => void;
  public py: number;
  public draggingCorner: boolean;
  @ViewChild("activeFixings", { static: true, read: ElementRef }) private activeFixings: ElementRef;

  @ViewChild(BmarkSummaryClientOrdersComponent)
  public bmarkSummaryClientOrdersComponent!: BmarkSummaryClientOrdersComponent;
  // subscribes to counts based on filtered ag-grid
  @ViewChild(BmarkSummaryHedgeOrdersComponent)
  public bmarkSummaryHedgeOrdersComponent!: BmarkSummaryHedgeOrdersComponent;

  @ViewChild(BmarkSummarySpotRiskContainerComponent)
  public bmarkSummarySpotRiskComponent!: BmarkSummarySpotRiskContainerComponent;

  @ViewChild(BmarkSummaryFwdRiskContainerComponent)
  public bmarkSummaryFwdRiskComponent!: BmarkSummaryFwdRiskContainerComponent;

  @ViewChild(BfxFixingBlotterAuditComponent)
  public bmarkFixingBlotterAuditComponent!: BfxFixingBlotterAuditComponent;

  @ViewChild(BfxHedgeOrderMonitorComponent)
  public bmarkHomComponent!: BfxHedgeOrderMonitorComponent;

  @ViewChild("settingsMenu")
  public settingsMenu!: BmarkSummarySettingsMenuComponent;

  @ViewChildren("subBlotterComponent")
  set subBlotterComponents(component: QueryList<ChildBlotterComponent>) {
    if (component != null) {
      this._subBlotterComponents = component;
      // NOTE: so that the blotter instance is detected in the blotterBar
      this.blotterInstance = this;
      this.cd.detectChanges();
    }
  }

  get subBlotterComponents() {
    if (this._subBlotterComponents == null) {
      return new QueryList();
    }
    return this._subBlotterComponents;
  }

  @ViewChild("fixings")
  public fixings!: Splitter;
  public fixingsPanelSizes: [number, number] = [50, 50];
  public readonly fixingsConfigVersion: string = "2022051001";

  @ViewChild("bottomBar")
  public bottomBar!: ElementRef;

  private subscriptions: Subscription[] = [];

  public static isFilterApplied(filterConfiguration: FilterConfiguration): boolean {
    return Array.isArray(filterConfiguration) && filterConfiguration.length > 0;
  }

  /**********************************************************************/
  /* CONSTRUCTOR */

  constructor(
    private translate: TranslateService,
    private bmarkManager: BmarkFixingsManagerService,
    protected layoutService: GoldenLayoutService,
    protected benchmarkBlotterOrderService: BmarkClientOrdersDataService,
    protected bmarkTableDefinition: BmarkClientOrdersTableDefinitionFactory,
    protected filterService: BfxBlotterFilterService,
    protected blotterFilterService: BmarkBlotterBaseFilteringService,
    protected activeUserProfileService: ActiveUserProfilePromiseService,
    protected userProfileService: BfxUserProfileService,
    protected profileActionService: ProfilePromiseActionService,
    protected tgService: TraderGroupLookupService,
    protected ccyService: CcyLookupService,
    protected cd: ChangeDetectorRef,
    protected hedgeOrderMonitorUtilService: BfxHedgeOrderMonitorUtilService,
    protected bmarkUtilService: BmarkSummaryUtilService,
    protected hedgeOrderContextMenuService: BmarkHedgeOrdersContextMenuService,
    protected bmarkContextMenuService: BmarkClientOrdersContextMenuService,
    protected systemConfigService: SystemConfigPromiseService,
    public viewFilterService: BmarkBlotterViewFilterSelectionService,
    protected notificationService: NotificationService,
    protected cubiqWorker: CubiqWorker,
    protected benchmarkDataService: BenchmarkDataPrimaryLookupService,
    public element: ElementRef,
  ) {
    super();
    this.blotterId = BmarkContainerBlotterId.BMARK_SUMMARY;

    let permissions = this.bmarkManager.getPermissions();
    if (permissions != null) {
      this.control.canViewSpotRisk =
        typeof permissions.canViewSpotRisk === "boolean"
          ? permissions.canViewSpotRisk
          : this.control.canViewSpotRisk;
      this.control.canViewFwdRisk =
        typeof permissions.canViewFwdRisk === "boolean"
          ? permissions.canViewFwdRisk
          : this.control.canViewFwdRisk;
      this.control.canViewClientOrders =
        typeof permissions.canViewClientOrders === "boolean"
          ? permissions.canViewClientOrders
          : this.control.canViewClientOrders;
      this.control.canViewHedgeOrders =
        typeof permissions.canViewHedgeOrders === "boolean"
          ? permissions.canViewHedgeOrders
          : this.control.canViewHedgeOrders;
      this.control.bmRiskRouteEnabled =
        typeof permissions.bmRiskRouteEnabled === "boolean"
          ? permissions.bmRiskRouteEnabled
          : this.control.bmRiskRouteEnabled;
      this.control.canViewBenchmarkAudit =
        typeof permissions.canViewBenchmarkAudit === "boolean"
          ? permissions.canViewBenchmarkAudit
          : this.control.canViewBenchmarkAudit;
    }

    this.systemConfigService
      .getSystemConfig(SysFeatures.manualRiskRouting)
      .then((value: string) => {
        this.control.manualRiskRouting = value != null && value.toUpperCase() == "Y";
      });

    this.control.tabsNumber += +this.control.canViewSpotRisk;
    this.control.tabsNumber += +this.control.canViewFwdRisk;
    this.control.tabsNumber += +this.control.canViewClientOrders;
    this.control.tabsNumber += +this.control.canViewHedgeOrders;
    this.control.tabsNumber += +this.control.canViewBenchmarkAudit;
    this.setupBottomMenuVisibilityFlags();

    this.hedgeOrderMonitorUtilService.hedgeOrderMonitorComponent$
      .pipe(
        takeUntil(this.onDestroy$),
        filter((val) => val != null),
      )
      .subscribe(this.handleHOMComponentSubscription.bind(this));
    this.hedgeOrderMonitorUtilService.blotterBarSync$
      .pipe(
        takeUntil(this.onDestroy$),
        filter((val) => val != null),
      )
      .subscribe(this.handleBlotterBarSyncSubscription.bind(this));

    this.systemConfigService.getSystemConfig(SysFeatures.bmAutoHedging).then((value: string) => {
      this.control.hedgingEnabled = value != null && value.toUpperCase() == "Y";
      this.control.canUseCircuitBreaker =
        this.control.hedgingEnabled && permissions.canUseCircuitBreaker;
    });
  }

  /**********************************************************************/
  /* INITIALIZE */
  ngOnInit() {
    this.initialize();
    this.bmarkManager.setInterfaceState(true);
    let subscription = this.quickSearchSubject
      .pipe(debounceTime(1000))
      .subscribe(this.onChangeSearchValue.bind(this, true));
    this.subscriptions.push(subscription);

    const closerFn = (event) => {
      if (
        this._activeContextMenuBlotter != null &&
        this._activeContextMenuBlotter !== event.ladderBlotter
      ) {
        if (this._activeContextMenuBlotter instanceof BmarkHedgeOrdersBlotterComponent)
          this.hedgeOrderContextMenuService.hideMenu(this._activeContextMenuBlotter);
        if (this._activeContextMenuBlotter instanceof BmarkClientOrdersBlotterComponent)
          this.bmarkContextMenuService.hideMenu(this._activeContextMenuBlotter);
      }
      this._activeContextMenuBlotter = event.ladderBlotter;
    };

    subscription = this.hedgeOrderContextMenuService.showMenuSubject.subscribe(closerFn);
    this.subscriptions.push(subscription);

    subscription = this.bmarkContextMenuService.showMenuSubject.subscribe(closerFn);
    this.subscriptions.push(subscription);
  }

  ngAfterViewInit() {
    this.viewInitComplete = true;
    this.bmarkManager.setInterfaceState(true);

    if (this.bmarkSummarySpotRiskComponent != null && this.control.canViewSpotRisk) {
      let hedgeDataProvider: BmarkHedgeOrdersDataService =
        this.bmarkSummaryHedgeOrdersComponent.bmarkHedgeOrdersBlotter.service;
      this.bmarkSummarySpotRiskComponent.bmarkSpotRiskBlotter.spotService.spotRiskModel.primeAndMonitorHedgeData(
        hedgeDataProvider,
      );
    }

    if (this.control.canViewClientOrders) {
      this.subscriptions.push(
        this.bmarkSummaryClientOrdersComponent.bmarkClientOrdersBlotter.gridDataUpdated$.subscribe(
          (gridData) => {
            this.label.tab.clientOrdersLabel = `${this.label.tab.clientOrdersLabelBase} (${gridData.displayedRowCount})`;
          },
        ),
      );
      this.subscriptions.push(
        this.bmarkSummaryClientOrdersComponent.bmarkClientOrdersBlotter.gridReady.subscribe(() => {
          if (this.bmarkSummaryClientOrdersComponent?.bmarkClientOrdersBlotter?.gridOptions) {
            this.bmarkSummaryClientOrdersComponent.bmarkClientOrdersBlotter.gridOptions.onColumnVisible =
              this.resetSearch.bind(this);
          }
        }),
      );
    }

    if (this.control.canViewHedgeOrders) {
      this.subscriptions.push(
        this.bmarkSummaryHedgeOrdersComponent.bmarkHedgeOrdersBlotter.gridDataUpdated$.subscribe(
          (gridData) => {
            this.label.tab.hedgeOrdersLabel = `${this.label.tab.hedgeOrdersLabelBase} (${gridData.displayedRowCount})`;
          },
        ),
      );
      this.subscriptions.push(
        this.bmarkSummaryHedgeOrdersComponent.bmarkHedgeOrdersBlotter.gridReady.subscribe(() => {
          if (this.bmarkSummaryHedgeOrdersComponent?.bmarkHedgeOrdersBlotter?.gridOptions) {
            this.bmarkSummaryHedgeOrdersComponent.bmarkHedgeOrdersBlotter.gridOptions.onColumnVisible =
              this.resetSearch.bind(this);
          }
        }),
      );
    }

    this.bmarkUtilService.summaryInstance = this;

    this.subscriptions.push(
      this.viewFilterService.selection$.subscribe((viewFilterType) => {
        //reapply filter configuration to pick up viewFilter
        setTimeout(
          (viewFilterType) => {
            this.data.viewFilterType = viewFilterType;
            let filterConfiguration = this.blotterFilterBar.filterConfiguration;
            this.filterSelectionChanged(filterConfiguration);
            this.data.viewFilterRequestPending = false;
            this.viewFilterTypeItems.forEach((item, index) => {
              if (viewFilterType === index) {
                item.styleClass = "active-filter";
              } else {
                item.styleClass = "";
              }
            });
          },
          4,
          viewFilterType,
        );
      }),
    );

    this.loadProfileSettings();
  }

  ngOnDestroy() {
    this.bmarkUtilService.summaryInstance = null;

    this.subscriptions.forEach((subscription) => subscription.unsubscribe());

    this.viewInitComplete = false;
    this.bmarkManager.setInterfaceState(false);
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
  protected initialize(): void {
    this.initializeTranslations();

    let subscription = this.bmarkManager.displayFixing$.subscribe((fixingData: BmarkFixingData) => {
      if (fixingData instanceof BmarkFixingData || fixingData == null) {
        // When fixing data arrives, the orders themselves may not be retrieved - this will only happen on a
        // fullupdate event
        this.data.activeFixingData = fixingData;
        this.data.activeFixingTitle = this.generateActiveFixingTitle(fixingData);
      }
    });
    this.subscriptions.push(subscription);

    subscription = this.bmarkManager.retrievingOrdersForFixing$.subscribe(() => {
      this.retrievingFixingData = true;
    });
    this.subscriptions.push(subscription);

    subscription = this.benchmarkBlotterOrderService.fullUpdateSubject.subscribe((_rows) => {
      this.retrievingFixingData = false;
    });
    this.subscriptions.push(subscription);

    subscription = this.bmarkManager.activeTab$.subscribe((activeTab: FixingActiveTab) => {
      if (
        typeof activeTab?.tabIndex === "number" &&
        activeTab.tabIndex >= 0 &&
        activeTab.tabIndex <= 4
      ) {
        this.data.tabIndex = activeTab.tabIndex;
        this.setupBottomMenuVisibilityFlags();
      }
    });
    this.subscriptions.push(subscription);

    subscription = this.bmarkManager.summaryAlertStatus$.subscribe(
      (alertStatus: BmarkSummaryAlertStatus) => {
        this.processAlertStatusUpdate(alertStatus);
      },
    );
    this.subscriptions.push(subscription);
  }

  private initializeTranslations(): void {
    this.label.retrievingOrders = this.translate.instant(this.label.retrievingOrdersKey);
    this.label.button.dealtViewLabel = this.translate.instant(this.label.button.dealtViewLabelKey);
    this.label.button.riskViewLabel = this.translate.instant(this.label.button.riskViewLabelKey);
    this.label.button.riskViewLabel = this.translate.instant(this.label.button.riskViewLabelKey);
    this.label.button.copyToClipboard = this.translate.instant(
      this.label.button.copyToClipboardKey,
    );
    //
    this.label.tab.spotRiskLabel = this.translate.instant(this.label.tab.spotRiskLabelKey);
    this.label.tab.clientOrdersLabel = this.label.tab.clientOrdersLabelKey = this.translate.instant(
      this.label.tab.clientOrdersLabelKey,
    );
    this.label.tab.hedgeOrdersLabel = this.label.tab.hedgeOrdersLabelBase = this.translate.instant(
      this.label.tab.hedgeOrdersLabelKey,
    );
    this.label.tab.auditLabel = this.translate.instant(this.label.tab.auditLabelBase);
  }

  /**
   * Init the boolean flags used by the view to show/hide different parts of the button bar. Should be called everytime
   * the active tab changes
   */
  private setupBottomMenuVisibilityFlags(): void {
    const tabType = this.bmarkManager.getActiveTabType();
    switch (tabType) {
      case FixingTabType.FWD_RISK:
      case FixingTabType.SPOT_RISK:
      case FixingTabType.CLIENT_ORDERS:
        this.control.dealtRiskVisible = true;
        this.control.typeFilterVisible = true;
        this.control.copyClipboardVisible = false;
        this.control.fwdRiskVisible = tabType == FixingTabType.FWD_RISK;
        break;
      case FixingTabType.HEDGE_ORDERS:
        this.control.dealtRiskVisible = false;
        this.control.typeFilterVisible = true;
        this.control.copyClipboardVisible = false;
        this.control.fwdRiskVisible = false;
        break;
      case FixingTabType.FIXING_AUDIT:
        this.control.dealtRiskVisible = false;
        this.control.typeFilterVisible = false;
        this.control.copyClipboardVisible = true;
        this.control.fwdRiskVisible = false;
        break;
    }
  }

  private loadProfileSettings(): void {
    this.activeUserProfileService.getActiveUserData().then((userData: ActiveUser) => {
      this.initializeActiveView(userData);
      this.control.isTrader = userData.isTrader();

      let amountViewType: AmountViewType = null;
      if (this.bmarkSummarySpotRiskComponent != null && this.control.canViewSpotRisk) {
        amountViewType = this.bmarkSummarySpotRiskComponent.initializeAmountView(userData);
      }
      if (this.bmarkSummaryFwdRiskComponent != null && this.control.canViewFwdRisk) {
        const fwdViewType = this.bmarkSummarySpotRiskComponent.initializeAmountView(userData);
        if (amountViewType == null) {
          amountViewType = fwdViewType;
        }
      }
      if (amountViewType != null) {
        // Let's ensure that both Spot and Forward risk rows are showing amounts in same convention - this is important
        // if a user moves from having access to only a single view type to both
        this.settingsMenu.amountViewType = amountViewType;
        this.setAmountViewType(amountViewType);
      }
    });
  }

  private initializeActiveView(userData: ActiveUser): void {
    // select active tab for client orders
    let setToRiskView = userData.isTrader();
    if (userData.profileSettings instanceof Map && userData.profileSettings.size > 0) {
      if (this.bmarkSummaryClientOrdersComponent) {
        let clientOrdersSavedData: any = userData.profileSettings.get(
          this.bmarkSummaryClientOrdersComponent.getProfileSettingKey(),
        );
        let clientOrdersParsedData =
          clientOrdersSavedData != null ? JSON.parse(clientOrdersSavedData) : null;
        setToRiskView =
          clientOrdersParsedData != null &&
          clientOrdersParsedData.hasOwnProperty("activeView") &&
          clientOrdersParsedData.activeView === BmarkClientOrdersBlotterMode.RISK;
      }
      const bmContainerConfigStr = userData.profileSettings.get(BM_CONTAINER_PROFILE_KEY);
      if (bmContainerConfigStr) {
        const bmContainerConfig = JSON.parse(bmContainerConfigStr);
        this.applyBMContainerConfiguration(bmContainerConfig);
      }
    }

    if (setToRiskView) {
      this.activateRiskView();
    } else {
      this.activateDealtView();
    }
    this.updateTableViewMenuItems();
  }

  /**
   * The method is used to dynamically
   * add and remove the BfxHedgeOrderMonitorComponent
   * to the subBlotter list in order to be synced with the BmarkSummaryBlotter
   *
   * @param homComponentObj - contains `isClosed` boolean and `homComponentObj` which is `this` of BfxHedgeOrderMonitorComponent
   */
  private handleHOMComponentSubscription(homUtilObject: any) {
    if (this.subBlotterComponents != null) {
      let resultsTree: any[] = homUtilObject.isClosed
        ? // remove from the list if it's there
          this.subBlotterComponents.filter(
            (item: any) =>
              !(
                item instanceof BfxHedgeOrderMonitorComponent &&
                item.instanceId === homUtilObject.homComponentObj.instanceId
              ),
          )
        : [...this.subBlotterComponents.toArray(), homUtilObject.homComponentObj];
      this.subBlotterComponents.reset(resultsTree);
    } else {
      // subBlotterComponents not yet initialized properly
    }
  }

  private handleBlotterBarSyncSubscription(subEventData: any) {
    this.blotterFilterBar.items = subEventData.items;
    let selectedItem = subEventData.selectedItem;
    let filterConfiguration = this.blotterFilterBar.filterConfiguration;
    this.syncBlotterFilterBarItemsListInOtherOpenedHOMs();
    this.cleanUpAndUpdateBlotterFilterBarItemList(selectedItem, filterConfiguration);
    if (this.filterSelectionChanged) {
      this.filterSelectionChanged(filterConfiguration);
    }
  }

  private handleFwdFilterChanged(filterValue: BmarkSummaryRiskFilterType) {
    logger.out.info(`Got Fwd Filter change: ${BmarkSummaryRiskFilterType[filterValue]}`);
    this.data.fwdRiskFilter = filterValue;
  }

  private syncBlotterFilterBarItemsListInOtherOpenedHOMs() {
    let hedgeOrderMonitorComponents: Array<ChildBlotterComponent> =
      this.subBlotterComponents.filter(
        (subComponent) => subComponent instanceof BfxHedgeOrderMonitorComponent,
      );

    if (hedgeOrderMonitorComponents != null && hedgeOrderMonitorComponents.length > 1) {
      hedgeOrderMonitorComponents.forEach((elem: any) => {
        elem.blotterFilterBar.items = this.blotterFilterBar.items;
        elem.blotterFilterBar.updateItems();
      });
    }
  }

  private cleanUpAndUpdateBlotterFilterBarItemList(selectedFilter: any, filterConfig: any[]) {
    this.blotterFilterBar.items.sort(this.compareItems);
    let index: number = filterConfig.indexOf(selectedFilter.filterId);
    if (index < 0 && selectedFilter.selected) {
      filterConfig.push(selectedFilter.filterId);
    } else if (index >= 0 && !selectedFilter.selected) {
      filterConfig.splice(index, 1);
    }
    this.blotterFilterBar.items = this.blotterFilterBar.items.filter(
      (item) => item.favorited || item.selected,
    );
  }

  // copied from BloterFilterBarComponent
  private compareItems(itemA: any, itemB: any): number {
    return itemA.selected == itemB.selected
      ? itemA.filterName.localeCompare(itemB.filterName)
      : itemA.selected
        ? -1
        : 1;
  }

  /**********************************************************************/
  /* PUBLIC METHODS */

  public resize(_$event: any): void {
    //
  }
  /**********************************************************************/
  /* BUTTON METHODS */

  public toggleSearch(_el?: any): void {
    this.showSearch = !this.showSearch;
    if (!this.showSearch) {
      this.searchValue = "";
      this.onChangeSearchValue(true);
    }
  }

  public resetSearch(): void {
    if (this.showSearch) {
      this.searchValue = "";
      this.onChangeSearchValue(true);
    }
  }

  public onChangeSearchValue(immidiate: boolean = false) {
    if (immidiate) {
      this.search.next();
      this.bmarkUtilService.quickFilterSubject.next(this.searchValue);
    } else {
      this.quickSearchSubject.next(undefined);
    }
  }

  public showFilterManager(event: Event): void {
    BfxBlotterFilterManagerComponent.openFilterManager(
      this.bmarkTableDefinition,
      this.translate,
      this.layoutService,
      FilterType.BlotterFilter,
      this.blotterId,
      event,
    );
    // this.bmarkClientOrdersBlotter.forEach(c => c.showFilterManager(event));
  }

  public hasAmountViewType(): boolean {
    // don't show if no permission to see spot risk
    return this.control.canViewSpotRisk || this.control.canViewFwdRisk;
  }

  public setAmountViewType(type: AmountViewType) {
    if (this.bmarkSummarySpotRiskComponent != null && this.control.canViewSpotRisk) {
      this.bmarkSummarySpotRiskComponent.setAmountViewType(type);
    }
    if (this.bmarkSummaryFwdRiskComponent != null && this.control.canViewFwdRisk) {
      this.bmarkSummaryFwdRiskComponent.setAmountViewType(type);
    }
  }

  public getAmountViewType(): AmountViewType {
    if (this.bmarkSummarySpotRiskComponent != null && this.control.canViewSpotRisk) {
      return this.bmarkSummarySpotRiskComponent.getAmountViewType();
    }

    if (this.bmarkSummaryFwdRiskComponent != null && this.control.canViewFwdRisk) {
      return this.bmarkSummaryFwdRiskComponent.getAmountViewType();
    }

    return null;
  }

  showSettings(event: Event) {
    this.layoutService.showSettings(USERSETTINGS_CATEGORY_BMARKSUMMARY, event);
  }

  /**********************************************************************/
  /* BUTTON METHODS */

  public handleTabChange($event: any): void {
    this.bmarkManager.setActiveTab($event.index);
  }

  public activateDealtView(): void {
    if (this.data.activeTableView !== BmarkClientOrdersBlotterMode.DEALT) {
      logger.out.info(`bms-c - switching to Dealt view`);
      this.data.activeTableView = BmarkClientOrdersBlotterMode.DEALT;

      this.benchmarkBlotterOrderService.setBlotterMode(this.data.activeTableView);
      this.updateTableViewMenuItems();
    }
  }

  public activateRiskView(): void {
    if (this.data.activeTableView !== BmarkClientOrdersBlotterMode.RISK) {
      logger.out.info(`bms-c - switching to Risk view`);
      this.data.activeTableView = BmarkClientOrdersBlotterMode.RISK;

      this.benchmarkBlotterOrderService.setBlotterMode(this.data.activeTableView);
      this.updateTableViewMenuItems();
    }
  }

  public updateTableViewMenuItems(): void {
    this.tableViewItems.forEach((item, index) => {
      if (this.data.activeTableView === index) {
        item.styleClass = "active-view";
      } else {
        item.styleClass = "";
      }
    });

    this.cd.detectChanges();
  }

  /**
   *
   * @param selectedTraderGroupId optional parameter which should be only populated if a trader group was selected
   * to route the selected risk positions to. This will invoke the hedge order monitor with the selected positions and
   * the selected trader group
   */
  public activateHedgeMonitor(selectedTraderGroupId: CubiqTS.Long = null): void {
    if (
      this.control.canViewHedgeOrders &&
      this.control.bmRiskRouteEnabled &&
      this.control.isTrader &&
      this.control.manualRiskRouting
    ) {
      let data: BfxHedgeOrderMonitorComponentData = new BfxHedgeOrderMonitorComponentData();

      data.fixingData = this.data.activeFixingData;
      data.bmarkSummaryData = this.data;
      data.blotterInstance = this.blotterInstance;
      data.blotterFilterBarData = this.blotterFilterBar;

      if (
        selectedTraderGroupId != null &&
        this.bmarkSummarySpotRiskComponent != null &&
        this.control.canViewSpotRisk
      ) {
        data.traderGroupId = selectedTraderGroupId;
        let gridOptions: GridOptions =
          this.bmarkSummarySpotRiskComponent.bmarkSpotRiskBlotter.gridOptions;
        if (gridOptions != null && gridOptions.api) {
          let selectedNodes: RowNode[] = gridOptions.api.getSelectedNodes();
          let spotRiskModel: SpotRiskModel =
            this.bmarkSummarySpotRiskComponent.bmarkSpotRiskBlotter.spotService.spotRiskModel;

          if (selectedNodes != null && selectedNodes.length > 0) {
            let selectedPairs: SpotRiskPair[] = selectedNodes
              .map((node: RowNode) => node.data)
              .map((model: any) => model.ccyPair)
              .map((ccyPair: string) => spotRiskModel.spotRiskData.get(ccyPair))
              .filter((spotRiskPair: SpotRiskPair) => spotRiskPair != null)
              .map((spotRiskPair) => this.createRoundedRisk(spotRiskPair));
            data.positions = selectedPairs;
          }
        }

        let traderGroup: CubiqTS.TradingGroupImpl =
          this.tgService.getTraderGroup(selectedTraderGroupId);

        if (data.positions != null && traderGroup != null) {
          let nonZeroPositions: Array<SpotRiskPair> = data.positions.filter(
            (position: SpotRiskPair) =>
              !this.hedgeOrderMonitorUtilService
                .roundToMillion(position.total, traderGroup)
                .isZero(),
          );
          if (nonZeroPositions.length == 0) {
            let payload = {
              messages: [this.translate.instant("cannot.hedge.warning.message")],
              title: "cannot.hedge.warning.title",
              showConfirmationMessage: false,
              showLogoutMessage: false,
            };

            let bfxDialogData: BfxDialogData = new BfxDialogData(
              BfxDialogCommand.DIALOG_GENERIC_OK,
              null,
              null,
              payload,
            );
            BfxHedgeOrderMonitorComponent.addDialog(bfxDialogData);
            return;
          } else {
            data.positions = nonZeroPositions;
          }
        }
      }

      BfxHedgeOrderMonitorComponentLauncher.launchHedgeOrderMonitor(
        data,
        this.layoutService,
        this.translate,
        event,
      );
    }
  }

  /**********************************************************************/
  /* PRIVATE METHODS */

  /**
   * By default, we remove all cent parts from our summary amounts to stop Pragma complaining
   * by creating a cloned risk with no cents.
   * These can be reinstanted manually inside the HOM, if necessary
   * @param spotRiskPair
   */
  private createRoundedRisk(spotRiskPair: SpotRiskPair): SpotRiskPair {
    const oldTotal: CubiqTS.BigNumber = spotRiskPair.total;
    const roundedTotal: CubiqTS.BigNumber = oldTotal.decimalPlaces(0, CubiqTS.BigNumber.ROUND_DOWN);
    const retPair = new SpotRiskPair(
      spotRiskPair.baseCCY,
      spotRiskPair.nonBaseCCY,
      null,
      roundedTotal,
    );
    return retPair;
  }

  private generateActiveFixingTitle(fixingData: BmarkFixingData): string {
    if (fixingData instanceof BmarkFixingData) {
      let lastRevalTitle: string = "";

      try {
        if (fixingData != null && fixingData.type == BmarkFixingType.ACTIVE) {
          lastRevalTitle = "\nLast Revalued: N/A";

          if (fixingData.lastRevalTime != null) {
            let lastRevalDisplayDate: string = fixingData.lastRevalTime.format("DD-MMM-YY");
            let lastRevalDisplayTime: string = fixingData.lastRevalTime.format("HH:mm");

            if (fixingData.displayDate != lastRevalDisplayDate) {
              lastRevalDisplayTime = `${lastRevalDisplayTime} ${lastRevalDisplayDate}`;
            }

            lastRevalTitle = `\nLast Revalued: ${lastRevalDisplayTime}`;
          }
        }
      } catch (e) {
        logger.out.error("Failed to construct last reval title information: " + e);
      }

      return `${this.translate.instant(fixingData.i18nFillStatusMessage())}${lastRevalTitle}`;
    }
    return "";
  }

  /**********************************************************************/
  /* METHODS - SCO/HCO ALERTS */

  private processAlertStatusUpdate(alertStatus: BmarkSummaryAlertStatus): void {
    // let hcoIds =
    //   this.control.alerts.hcoIds != null
    //     ? Array.from(this.control.alerts.hcoIds).toString()
    //     : "NULL";
    // let scoIds =
    //   this.control.alerts.scoIds != null
    //     ? Array.from(this.control.alerts.scoIds).toString()
    //     : "NULL";
    // let localState = `hcoState: ${this.control.alerts.hco} | hcoIds: ${hcoIds} | scoState: ${this.control.alerts.sco} | scoIds: ${scoIds}`;
    //logger.out.info(`BMARK: BM-Container received Alert Status update:`, alertStatus, `| local state: ${localState}`);
    if (alertStatus instanceof BmarkSummaryAlertStatus) {
      // determine if it needs to flash
      let hcoFlash: boolean =
        !this.control.active &&
        alertStatus.hco_alert &&
        (this.control.alerts.hcoIds == null ||
          alertStatus.hco_ids.size > this.control.alerts.hcoIds.size ||
          (alertStatus.hco_ids.size === this.control.alerts.hcoIds.size &&
            !Array.from(alertStatus.hco_ids).every((e) => this.control.alerts.hcoIds.has(e))));
      let scoFlash: boolean =
        !this.control.active &&
        alertStatus.sco_alert &&
        (this.control.alerts.scoIds == null ||
          alertStatus.sco_ids.size > this.control.alerts.scoIds.size ||
          (alertStatus.sco_ids.size === this.control.alerts.scoIds.size &&
            !Array.from(alertStatus.sco_ids).every((e) => this.control.alerts.scoIds.has(e))));
      this.control.alerts.flash =
        this.control.alerts.flash || hcoFlash || (!alertStatus.hco_alert && scoFlash);

      //logger.out.info(`BMARK: BM-Container: FLASH FG: ${this.control.alerts.flash ? 'FLASH' : 'NO FLASH'}`);
      // assign values
      this.control.alerts.hco = alertStatus.hco_alert;
      this.control.alerts.hcoIds = alertStatus.hco_ids;
      this.control.alerts.sco = alertStatus.sco_alert;
      this.control.alerts.scoIds = alertStatus.sco_ids;
      this.control.alerts.badgeCount = this.control.alerts.hco
        ? alertStatus.hco_orders
        : this.control.alerts.sco
          ? alertStatus.sco_orders
          : 0;
      let alertStatusState = alertStatus.hco_alert
        ? this.control.alerts.flash
          ? GLComponentAlertStatus.HCO_FLASH
          : GLComponentAlertStatus.HCO
        : alertStatus.sco_alert
          ? this.control.alerts.flash
            ? GLComponentAlertStatus.SCO_FLASH
            : GLComponentAlertStatus.SCO
          : GLComponentAlertStatus.NONE;
      this.layoutService.updateBadge(this, this.control.alerts.badgeCount, alertStatusState);
    }
  }

  public componentActivated(): void {
    //logger.out.info(`BMARK: BM-Container: Component ACTIVATE TRIGGERED`);
    this.control.active = true;
    this.control.alerts.flash = false;
  }

  public undocked(): void {
    this.componentDeactivated();
  }

  public componentDeactivated(): void {
    //logger.out.info(`BMARK: BM-Container: Component DEACTIVATE TRIGGERED`);
    this.control.active = false;
  }

  public getAlertStatus(): GLComponentAlertStatus {
    let flashState: GLComponentAlertStatus;

    if (this.control.alerts.hco && this.control.alerts.flash) {
      flashState = GLComponentAlertStatus.HCO_FLASH;
    } else if (this.control.alerts.sco && this.control.alerts.flash) {
      flashState = GLComponentAlertStatus.SCO_FLASH;
    } else if (this.control.alerts.hco) {
      flashState = GLComponentAlertStatus.HCO;
    } else if (this.control.alerts.sco) {
      flashState = GLComponentAlertStatus.SCO;
    } else if (this.control.active) {
      flashState = GLComponentAlertStatus.NONE;
    } else {
      flashState = GLComponentAlertStatus.NONE;
    }
    //logger.out.info(`BMARK: BM-Container: ALERT STATUS -> Component: ${this.control.active ? 'ACTIVE': 'NOT ACTIVE'} |  flash: ${ this.control.alerts.flash} | State: ${GLComponentAlertStatus[flashState]} | badgeCount: ${this.control.alerts.badgeCount}`);
    return flashState;
  }

  public getBadgeNumber(): number {
    return this.control.alerts.badgeCount;
  }

  /*************************************/
  /* PROFILE METHODS */

  public createBlotterProfileSettings(
    profileKey: string,
    profileSettingsToBeSaved: any,
  ): CubiqTS.ProfileSettingImpl {
    let profile = {
      version: profileSettingsToBeSaved.configVersion,
      filterConfiguration: profileSettingsToBeSaved.filterConfiguration,
      blotterId: profileSettingsToBeSaved.blotterId,
      columnsConfiguration: profileSettingsToBeSaved.columnsConfiguration,
      sortingModel: profileSettingsToBeSaved.sortingModel,
      activeView: profileSettingsToBeSaved.activeView,
      amountViewType:
        profileSettingsToBeSaved.amountViewType /* NB : Should not known here - should be cumulative in a map - not an object */,
    };

    const retSetting = this.createProfileSetting(profileKey, profile);
    return retSetting;
  }

  /**
   * Convert profile value to a JSON string and create profile setting with associated key
   * @param profileKey
   * @param profile
   */
  private createProfileSetting(profileKey: string, profile: any): CubiqTS.ProfileSettingImpl {
    return this.userProfileService.createProfileSettingObject(profileKey, JSON.stringify(profile));
  }

  /**
   * Asynchronously propagate the settings to save the server and update all local profiles on return
   * @param profileKey
   * @param profile
   */
  private saveSettings(profileSettings: CubiqTS.ProfileSettingImpl[]) {
    this.profileActionService.updateProfile(profileSettings, false).then((_value: any) => {
      this.activeUserProfileService.getActiveUserData().then((userData: ActiveUser) => {
        if (userData.profileSettings != null) {
          profileSettings.forEach((setting) => {
            userData.profileSettings.set(setting.profileSettingKey, setting.profileSettingValue);
          });
          this.activeUserProfileService.setActiveUserData(userData);
        }
      });
    });
  }

  /**
   * Create a config object that stores all Container-level settings for BM Summary - for the moment
   * the only value actually used here on loading is fwdRiskFilter
   * @returns
   */
  private createBMContainerConfiguration() {
    return {
      version: BM_CONFIG_VERSION,
      fwdRiskFilter: this.data.fwdRiskFilter,
      // autoHedgingEnabled: this.data.autoHedgingEnabled,
      // activeTab: this.bmarkManager.getActiveTabType(),
      // viewFilterType: this.data.viewFilterType,
      // amountType: this.getAmountViewType(),
      // activeView: this.data.activeTableView,
    };
  }

  private applyBMContainerConfiguration(config) {
    if (config && config.version == BM_CONFIG_VERSION) {
      this.data.fwdRiskFilter = config.fwdRiskFilter;
      // FOr the moment we can leave the other settings since the original impl restores these values elsewhere
    }
  }

  public getComponentConfiguration(): any {
    let allConfigs: Array<any> = new Array<any>();

    const profileSettings: CubiqTS.ProfileSettingImpl[] = [];

    // Overall values
    const bmConfiguration = this.createBMContainerConfiguration();
    profileSettings.push(this.createProfileSetting(BM_CONTAINER_PROFILE_KEY, bmConfiguration));

    // client orders
    if (this.bmarkSummaryClientOrdersComponent != null && this.control.canViewClientOrders) {
      let configuration = this.bmarkSummaryClientOrdersComponent.getClientOrdersConfiguration();
      if (configuration != null) {
        const clientOrdersConfig = configuration;
        clientOrdersConfig.activeView = this.data.activeTableView;
        const profileSetting = this.createBlotterProfileSettings(
          this.bmarkSummaryClientOrdersComponent.getProfileSettingKey(),
          clientOrdersConfig,
        );
        profileSettings.push(profileSetting);
        allConfigs.push(clientOrdersConfig);
      }
    }
    // spot risk
    if (this.bmarkSummarySpotRiskComponent != null && this.control.canViewSpotRisk) {
      let configuration = this.bmarkSummarySpotRiskComponent.getRiskConfiguration();
      if (configuration != null) {
        const spotRiskConfig = configuration;
        spotRiskConfig.activeView = this.data.activeTableView;
        const profileSetting = this.createBlotterProfileSettings(
          this.bmarkSummarySpotRiskComponent.getProfileSettingKey(),
          spotRiskConfig,
        );
        profileSettings.push(profileSetting);
        allConfigs.push(spotRiskConfig);
      }
    }
    // fwd risk
    if (this.bmarkSummaryFwdRiskComponent != null && this.control.canViewFwdRisk) {
      let configuration = this.bmarkSummaryFwdRiskComponent.getRiskConfiguration();
      if (configuration != null) {
        const fwdRiskConfig = configuration;
        fwdRiskConfig.activeView = this.data.activeTableView;
        const profileSetting = this.createBlotterProfileSettings(
          this.bmarkSummaryFwdRiskComponent.getProfileSettingKey(),
          fwdRiskConfig,
        );
        profileSettings.push(profileSetting);
        allConfigs.push(fwdRiskConfig);
      }
    }
    // hedge orders
    if (this.bmarkSummaryHedgeOrdersComponent != null && this.control.canViewHedgeOrders) {
      let configuration = this.bmarkSummaryHedgeOrdersComponent.getHedgeOrdersConfiguration();
      if (configuration != null) {
        const hedgeOrdersConfig = configuration;
        hedgeOrdersConfig.activeView = this.data.activeTableView;
        const profileSetting = this.createBlotterProfileSettings(
          this.bmarkSummaryHedgeOrdersComponent.getProfileSettingKey(),
          hedgeOrdersConfig,
        );
        profileSettings.push(profileSetting);
        allConfigs.push(hedgeOrdersConfig);
      }
    }
    //bm Audit
    if (this.bmarkFixingBlotterAuditComponent != null && this.control.canViewBenchmarkAudit) {
      let configuration = this.bmarkFixingBlotterAuditComponent.getBMAuditConfiguration();
      if (configuration != null) {
        const bmAuditConfig = configuration;
        bmAuditConfig.activeView = this.data.activeTableView;
        const profileSetting = this.createBlotterProfileSettings(
          this.bmarkFixingBlotterAuditComponent.getProfileSettingKey(),
          bmAuditConfig,
        );
        profileSettings.push(profileSetting);
        allConfigs.push(bmAuditConfig);
      }
    }
    // fixing panel size
    if (this.fixings != null) {
      let configuration = {
        configVersion: this.fixingsConfigVersion,
        panelSizes: JSON.stringify(this.fixings.panelSizes),
      };
      const profileSetting = this.createBlotterProfileSettings(
        ProfileSettingKeys.userBmarkSummaryFixingPanelSizes,
        configuration,
      );
      profileSettings.push(profileSetting);
      allConfigs.push(configuration);
    }
    this.saveSettings(profileSettings);
    return allConfigs;
  }

  restoreFromConfiguration(configs: any) {
    if (configs != null) {
      if (Array.isArray(configs) && configs.length > 0) {
        let mappedFilterConfigurations = [];
        configs.forEach((config) => {
          if (
            config.hasOwnProperty("filterConfiguration") &&
            Array.isArray(config.filterConfiguration)
          ) {
            config.filterConfiguration.forEach((el) => mappedFilterConfigurations.push(el));
          }

          if (
            config.hasOwnProperty("panelSizes") &&
            config.configVersion === this.fixingsConfigVersion
          ) {
            try {
              this.fixingsPanelSizes = JSON.parse(config.panelSizes);
            } catch (e) {
              logger.out.warn(
                `${this.constructor.name}.restoreFromConfiguration() ` +
                  `failed to restore fixing panel sizes`,
              );
            }
          }
        });
        this.filterConfiguration = [...new Set(mappedFilterConfigurations)];
        this.cd.detectChanges();
      } else {
        this.filterConfiguration = [];
        logger.out.error(
          `${this.constructor.name}.restoreFromConfiguration() ` +
            `Invalid profile configuration saved ` +
            `- Restored configuration is empty or not of correct type.`,
        );
      }
      this.filterSelectionChanged(this.filterConfiguration);
    } else {
      logger.out
        .error(`${this.constructor.name}.restoreFromConfiguration() : Invalid profile configuration saved
															- Restored configuration is undefined.`);
    }
  }

  /**
   * BLOTTER FILTERABLE METHODS
   */

  getBlotterId(): string {
    return this.blotterId;
  }
  getBlotterType(): CubiqTS.BlotterType {
    return CubiqTS.BlotterType.ORDER;
  }

  getFilterConfiguration() {
    let filterConfigToBeReturned: any[];
    let filterConfigurations: any[] = this.subBlotterComponents.map((c) =>
      c.getFilterConfiguration(),
    );
    const emptyFilterConfigurations = filterConfigurations.some((el: any) => el == null);

    filterConfigurations = emptyFilterConfigurations
      ? this.filterConfiguration
      : filterConfigurations;
    let filterConfigFromProfile = this.blotterFilterService.getFilterConfigurationFromProfile();
    filterConfigToBeReturned = BmarkSummaryContainerComponent.isFilterApplied(
      filterConfigFromProfile,
    )
      ? filterConfigFromProfile
      : [...new Set(filterConfigurations)];
    return filterConfigToBeReturned;
  }

  setBlotterFilterComponent(blotterFilter: BfxBlotterFilterBarComponent): void {
    this.subBlotterComponents.forEach((c) => c.setBlotterFilterComponent(blotterFilter));
    this.blotterFilterBar = blotterFilter;
  }

  filterSelectionChanged(filterConfiguration: FilterConfiguration): void {
    this.blotterFilterService.updateFilterSelection(filterConfiguration);
    this.subBlotterComponents.forEach((c) => c.filterSelectionChanged(filterConfiguration));
    this.triggerBadgeUpdateSummaryAlertStatus(filterConfiguration);
  }

  private triggerBadgeUpdateSummaryAlertStatus(filterConfiguration: FilterConfiguration) {
    this.triggerAppliedFilterStatus(filterConfiguration);
    this.bmarkManager.reRunUpdateSummaryAlertStatus();
  }
  private triggerAppliedFilterStatus(filterConfiguration: FilterConfiguration): void {
    let filterAppliedStatus: FilterAppliedStatusModel = new FilterAppliedStatusModel(
      BmarkSummaryContainerComponent.isFilterApplied(filterConfiguration),
      filterConfiguration,
      this.data.viewFilterType,
    );
    this.bmarkUtilService.isFilterApplied(filterAppliedStatus);
  }

  selectViewFilterAll() {
    if (
      this.data.viewFilterRequestPending === false &&
      BmarkSummaryViewFilterType.ALL !== this.data.viewFilterType
    ) {
      logger.out.info(`bms-c - viewing all orders`);
      this.data.viewFilterRequestPending = true;
      this.viewFilterService.next(BmarkSummaryViewFilterType.ALL);
    }
  }

  selectViewFilterActive() {
    if (
      this.data.viewFilterRequestPending === false &&
      BmarkSummaryViewFilterType.ACTIVE !== this.data.viewFilterType &&
      this.data.activeFixingData != null
    ) {
      logger.out.info(`bms-c - viewing Active orders only`);
      this.data.viewFilterRequestPending = true;
      this.viewFilterService.next(BmarkSummaryViewFilterType.ACTIVE);
    }
  }

  selectViewFilterFilled() {
    if (
      this.data.viewFilterRequestPending === false &&
      BmarkSummaryViewFilterType.FILLED !== this.data.viewFilterType &&
      this.data.activeFixingData != null
    ) {
      logger.out.info(`bms-c - viewing Filled orders only`);
      this.data.viewFilterRequestPending = true;
      this.viewFilterService.next(BmarkSummaryViewFilterType.FILLED);
    }
  }

  shouldShowTemperatureFilter(): boolean {
    return false;
  }

  hasCellRenderedSelection(): boolean {
    return false;
  }

  refreshSelectionCells(): void {
    this.subBlotterComponents.forEach((c) => c.refreshSelectionCells());
  }

  get bottomBarTitle(): string {
    return `${
      BmarkSummaryViewFilterType.ALL === this.data.viewFilterType
        ? "All "
        : BmarkSummaryViewFilterType.ACTIVE === this.data.viewFilterType
          ? "Active "
          : BmarkSummaryViewFilterType.FILLED === this.data.viewFilterType
            ? "Filled "
            : ""
    }${
      BmarkClientOrdersBlotterMode.DEALT === this.data.activeTableView
        ? "Dealt View"
        : BmarkClientOrdersBlotterMode.RISK === this.data.activeTableView
          ? "Risk View"
          : ""
    }`;
  }

  /**
   * Copy the active view to clipboard - currently uonly supported for Audit view
   */
  public copyToClipboard() {
    logger.out.info("Copying to clipboard");
    if (this.bmarkManager.getActiveTabType() === FixingTabType.FIXING_AUDIT) {
      this.bmarkFixingBlotterAuditComponent.actionExport_copyToClipboard();
    } else {
      logger.out.info("Cannot copy - illegal tab selected");
    }
  }

  // Splitter methods
  public onResizeClick(event: MouseEvent): void {
    this.mouseMoveHandler = this.onDragMove.bind(this);
    this.mouseUpHandler = this.onDragStop.bind(this);
    this.draggingCorner = true;
    this.py = event.clientY;

    event.preventDefault();
    event.stopPropagation();

    // AJB : cant't seem to use HostListener here as uses Main window in OpenFin
    if (this.element?.nativeElement) {
      let document: Document = DomHandler.getDocument(this.element.nativeElement);
      if (document) {
        document.addEventListener("mousemove", this.mouseMoveHandler);
        document.addEventListener("mouseup", this.mouseUpHandler);
      }
    }
  }

  private onDragMove(event: MouseEvent): void {
    if (!this.draggingCorner) {
      return;
    }
    let offsetY = event.clientY - this.py;

    this.height += offsetY;
    this.py = event.clientY;
  }

  private onDragStop(event?: MouseEvent): void {
    // this.draggingWindow = false;
    this.draggingCorner = false;
    this.height = this.activeFixings?.nativeElement?.offsetHeight;

    // Remove explicit mouse listeners
    if (this.element?.nativeElement) {
      let document: Document = DomHandler.getDocument(this.element.nativeElement);
      if (document) {
        document.removeEventListener("mousemove", this.mouseMoveHandler);
        document.removeEventListener("mouseup", this.mouseUpHandler);
      }
    }
  }

  shouldShowStateFilter(): boolean {
    return false;
  }

  public getTabIdentifier(): string {
    return "BmarkSummaryContainerComponent";
  }
}
